import React, { version } from "react";
import { version as reactDomVersion } from "react-dom";
import { MainRoute } from "./navigation/Routes/MainRoute";
import { Auth } from "aws-amplify";
import "@aws-amplify/ui-react/styles.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import {
  useIsDVCInitialized,
  withDVCProvider,
} from "@devcycle/devcycle-react-sdk";
import cn from "classnames";

import {
  API_BASE,
  ThemeContextProvider,
  useThemeContext,
  applyInitialBackgroundColor,
} from "@iliotech/data-wire";
import { Loader } from "@iliotech/component-library";
import "@iliotech/themes/lib/illio-theme.scss";
import "@progress/kendo-theme-default/dist/all.css";
import { configureLocalisation } from "./initialisation-scripts/configureLocalisation";
import { configureLocalhostEnvironmentVariables } from "./initialisation-scripts/configureLocalhostEnvironmentVariables";
import { useDomainConfig } from "./hooks/useDomainConfig";

console.log({
  importMetaEnv: import.meta.env,
  windowEnv: window.environmentSettings,
});

configureLocalisation();
configureLocalhostEnvironmentVariables();
const devCycleEnv = window.environmentSettings.devcycleEnv;
console.log("Consumer react version", version);
console.log("Consumer react dom version", reactDomVersion);

// axios.defaults.headers["Cookie"] = "";
const API_URL = API_BASE;
const awsconfig = {
  aws_project_region: window.environmentSettings?.awsRegionName,
  aws_cognito_region: window.environmentSettings?.awsCognitoRegionName,
  oauth: {},
};
const isDomo = ((window.environmentSettings?.rootUrl as string) || "").includes(
  "pms"
);
console.log({
  isDomo,
  rootUrl: window.environmentSettings?.rootUrl,
  hostname: window.location.hostname,
});
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: true, // Refetch *stale* queries when mounting a component that invokes useQuery
      refetchOnWindowFocus: false, // Refetch *stale* queries when user comes back from another window
      staleTime: 5 * 60 * 60 * 1000,
    }, // Queries are considered stale after 3 hours (5[h] * 60[m] * 60[s] * 1000ms)
  },
});

function App() {
  const [ready, setReady] = React.useState(false);

  // Apply initial background color immediately
  React.useEffect(() => {
    applyInitialBackgroundColor();
  }, []);

  // Configure domain-specific favicon and title
  useDomainConfig();

  /**
   * Side effect to load third party scripts (e.g. hubspot, userpilot, etc)
   */
  React.useEffect(() => {
    // injectScripts();
  }, []);
  const devCycleReady = useIsDVCInitialized();

  React.useEffect(() => {
    fetch(API_URL + "/api/public/frontend/properties", { method: "GET" })
      .then(async (response) => {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const json = await response?.json();
          // console.log({json})
          return json;
        } else {
          console.error("UNABLE TO LOAD POOL ID");
          return {
            pid: window.environmentSettings?.fallBackCognitoPid,
            acid: window.environmentSettings?.fallbackCognitoAcid,
          };
        }
      })
      .then((result) => {
        let environment = window.environmentSettings.env || "wealth";
        const isWhatIfApp = location.hostname.includes("whatif");

        // eslint-disable-next-line no-restricted-globals
        const isLocalhost =
          location.hostname === "localhost" ||
          location.hostname === "127.0.0.1";
        const host = environment === "prod" ? "wealth" : environment;
        const domain = isDomo
          ? environment === "stage"
            ? "pms-stage.domo.club"
            : "pms.domo.club"
          : isLocalhost
          ? "localhost"
          : environment
          ? host + ".illio.com"
          : "illio.com";
        console.log({ domain });
        const whatIfAppDomain = "whatif-app-" + domain;
        console.log("cookieDomain:" + domain);
        console.log("environment:" + environment);
        console.log("isWhatIfApp:" + isWhatIfApp);
        console.log("whatIfAppDomain:" + whatIfAppDomain);

        const configuration = {
          ...awsconfig,
          aws_user_pools_id: result.pid,
          aws_user_pools_web_client_id: result.acid,
          cookieStorage: {
            domain: isWhatIfApp ? whatIfAppDomain : domain,
          },
        };
        Auth.configure(configuration);
        setReady(true);
      })
      .catch(() => null);
  }, []);

  return (
    <div className={cn("illio-consumer-app")}>
      <ThemeContextProvider>
        {!ready || !devCycleReady ? (
          <Loader fullScreen />
        ) : (
          <QueryClientProvider client={queryClient}>
            <MainRoute />
            <ReactQueryDevtools
              initialIsOpen={false}
              position={"bottom-left"}
              toggleButtonProps={{
                style: {
                  position: "absolute",
                  bottom: 10,
                  left: 170,
                  zIndex: 99999,
                },
              }}
            />
          </QueryClientProvider>
        )}
      </ThemeContextProvider>
    </div>
  );
}

const injectScripts = () => {
  const hubspotId = window.environmentSettings.hubspotId! as string;

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.id = "hs-script-loader";
  script.async = true;
  script.defer = true;
  script.src = `//js.hs-scripts.com/${hubspotId}.js`;
  document.head.appendChild(script);
  // <script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/8361594.js"></script>
};

console.log("MOVE CONFIG TO ENV FROM HERE");
export default withDVCProvider({
  envKey: devCycleEnv as string,
  user: { isAnonymous: true },
})(App);
